import { Asset, AssetType, FolderContent, VisualSearchMatch } from '@/api/generatedApi';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { SearchInput } from '@/components/Search/SearchInput';
import { useTextSearch } from '@/hooks/api/crossTenant/textsearch/useTextSearch';
import { useFetchFolderContents } from '@/hooks/api/folders/useFetchFolderContents';
import { useFolderHierarchy } from '@/hooks/api/folders/useFolderHierarchy';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography, styled } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { comparePageColumns } from '@/components/ComparePage/columnDefinitions';

const Container = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  transition: 'width 0.3s ease, opacity 0.3s ease',
}));

const Header = styled(Box)(({ theme }) => ({
  width: 360,
  padding: theme.spacing(1, 2, 2, 2),
  minWidth: 0,
}));

const Description = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  maxWidth: '85%',
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const FolderNavContainer = styled(Box)({
  overflow: 'hidden',
  transition: 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease',
  marginBottom: 0,
});

const FolderNavInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const FolderName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  fontSize: '0.875rem',
  cursor: 'default',
}));

const GridContainer = styled(Box)(({ theme }) => ({
  width: 360,
  flex: 1,
  padding: theme.spacing(0, 2, 3, 2),
  overflow: 'hidden',
  minWidth: 0,
  flexDirection: 'column',
  position: 'relative',
}));

const StyledDataGrid = styled(Box)({
  userSelect: 'none',
  transition: 'padding-bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiDataGrid-cell': {
    cursor: 'pointer',
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row': {
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(56, 119, 85, 0.1)',
  },
  '& .MuiDataGrid-row.selected-row': {
    backgroundColor: 'rgba(56, 119, 85, 0.15)',
    '&:hover': {
      backgroundColor: 'rgba(56, 119, 85, 0.2)',
    },
  },
  '& .MuiDataGrid-row.disabled-row': {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '& .MuiDataGrid-row.disabled-row .MuiDataGrid-cell': {
    cursor: 'not-allowed',
  },
  '& .MuiTooltip-popper': {
    transitionDelay: '700ms !important',
  },
});

const SelectionBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 24,
  left: 16,
  right: 16,
  overflow: 'hidden',
  transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
  backgroundColor: 'rgba(56, 119, 85, 0.95)',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  cursor: 'default',
}));

const SelectionBarText = styled(Typography)({
  color: 'white',
  fontWeight: 500,
  cursor: 'default',
});

const ClearButton = styled(Button)({
  color: 'white',
  textTransform: 'none',
  minWidth: 'auto',
  padding: '4px 8px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

export const ComparePageSecondaryNav = ({
  selectedAssets,
  onAssetClick,
  onClearAll,
  maxAssets,
  isCollapsed = false,
}: {
  selectedAssets: Asset[];
  onAssetClick: (asset: Asset) => void;
  onClearAll: () => void;
  maxAssets: number;
  isCollapsed?: boolean;
}) => {
  const gridApi = useGridApiRef();
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();
  const [folderHistory, setFolderHistory] = useState<Array<{ id: string; name: string }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);

  const { fetchData, error: folderError } = useFetchFolderContents({ folderId: currentFolderId });
  const { hierarchy } = useFolderHierarchy({ folderId: currentFolderId });

  const {
    data: searchData,
    error: searchError,
    isLoading: searchLoading,
  } = useTextSearch({
    searchQuery: searchQuery,
    page: 1,
    perPage: DATA_GRID_LAZY_PAGE_SIZE,
    enabled: isSearchMode && searchQuery.trim().length > 0,
  });

  const currentFolderName = hierarchy.length > 0 ? hierarchy.at(-1)!.name : '';
  const fullPath = hierarchy.length > 0 ? hierarchy.map(f => f.name).join(' / ') : '';

  const handleRowClick = (parameters: { row: FolderContent }) => {
    if (parameters.row.contentType === 'folder') {
      const folder = parameters.row as { id: string; name: string; contentType: 'folder' };
      setCurrentFolderId(folder.id);
      setFolderHistory(previous => [...previous, { id: folder.id, name: folder.name }]);
      return;
    }

    const asset = parameters.row as Asset;
    if (asset.type === AssetType.Document) {
      return;
    }

    const isSelected = selectedAssets.some(a => a.id === asset.id);
    if (!isSelected && selectedAssets.length >= maxAssets) {
      return;
    }

    onAssetClick(asset);
  };

  const handleBack = () => {
    if (folderHistory.length > 0) {
      const newHistory = folderHistory.slice(0, -1);
      setFolderHistory(newHistory);
      setCurrentFolderId(newHistory.length > 0 ? newHistory.at(-1)!.id : undefined);
    } else {
      setCurrentFolderId(undefined);
      setFolderHistory([]);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setIsSearchMode(query.trim().length > 0);
  };

  const getRowClassName = (parameters: { row: FolderContent }) => {
    const classes: string[] = [];

    if (parameters.row.contentType === 'asset') {
      const asset = parameters.row as Asset;
      const isSelected = selectedAssets.some(a => a.id === asset.id);

      if (asset.type === AssetType.Document) {
        classes.push('disabled-row');
      }

      if (isSelected) {
        classes.push('selected-row');
      }

      if (!isSelected && selectedAssets.length >= maxAssets && asset.type !== AssetType.Document) {
        classes.push('disabled-row');
      }
    }

    return classes.join(' ');
  };

  const currentError = isSearchMode ? searchError : folderError;
  const currentLoading = isSearchMode ? searchLoading : false;

  const fetchCurrentData = async (parameters: { page: number; perPage: number }) => {
    if (isSearchMode) {
      if (searchData?.matches) {
        return {
          contents: searchData.matches.map((match: VisualSearchMatch) => ({
            ...match.asset,
            contentType: 'asset' as const,
          })),
          pageData: {
            total: searchData.pageData?.total || 0,
          },
        };
      }
      return { contents: [], pageData: { total: 0 } };
    } else {
      return fetchData(parameters);
    }
  };

  return (
    <Container
      sx={{
        width: isCollapsed ? 120 : 360,
      }}
    >
      <Header
        sx={{
          display: isCollapsed ? 'none' : 'block',
        }}
      >
        <Description variant='body2'>
          Select up to {maxAssets} assets to compare. Click assets to add them. Documents cannot be selected.
        </Description>
        <SearchContainer>
          <SearchInput
            initialValue={searchQuery}
            onSearch={handleSearch}
            placeholder='Search assets...'
            sx={{
              '& .MuiInputBase-root': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                backgroundColor: 'background.paper',
                padding: '8px 12px',
                fontSize: '0.875rem',
              },
              '& > div:last-child': {
                display: 'none',
              },
            }}
          />
        </SearchContainer>
        <FolderNavContainer
          sx={{
            maxHeight: currentFolderId && !isSearchMode ? '40px' : 0,
            opacity: currentFolderId && !isSearchMode ? 1 : 0,
          }}
        >
          <FolderNavInner>
            <BackButton size='small' onClick={handleBack}>
              <ArrowBack sx={{ fontSize: '18px' }} />
            </BackButton>
            <Tooltip title={fullPath}>
              <FolderName variant='body2'>{currentFolderName}</FolderName>
            </Tooltip>
          </FolderNavInner>
        </FolderNavContainer>
      </Header>
      <GridContainer
        sx={{
          display: isCollapsed ? 'none' : 'flex',
        }}
      >
        <StyledDataGrid
          sx={{
            paddingBottom: selectedAssets.length > 0 ? '56px' : 0,
          }}
        >
          <DataGridLazy<FolderContent, { contents: FolderContent[] }>
            apiRef={gridApi}
            columns={comparePageColumns}
            fetchRows={fetchCurrentData}
            dataKey='contents'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={currentLoading}
            localeText={{
              noRowsLabel: isSearchMode ? 'No search results found' : 'No contents available',
            }}
            error={currentError}
            density='compact'
            disableFilter={true}
            getRowClassName={getRowClassName}
            onRowClick={handleRowClick}
            slotProps={{
              basePopper: {
                onCompositionStart: event => {
                  event.preventDefault();
                },
              },
            }}
          />
        </StyledDataGrid>
        <SelectionBar
          sx={{
            maxHeight: selectedAssets.length > 0 ? '48px' : 0,
            opacity: selectedAssets.length > 0 ? 1 : 0,
          }}
        >
          <SelectionBarText variant='body2'>
            {selectedAssets.length} of {maxAssets} {selectedAssets.length === 1 ? 'asset' : 'assets'} selected
          </SelectionBarText>
          <ClearButton onClick={onClearAll}>Clear</ClearButton>
        </SelectionBar>
      </GridContainer>
    </Container>
  );
};
